// Basic
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
// Store and actions
import actionsPlans from '../../../api/actionsPlans';
import useRootOrganizationStore from '@/app/store/rootOrganization';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Switch from '@/common-utils/components/ui/switch';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { Icon } from '@radix-ui/react-select';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import ICONS from '@/common-utils/enums/icons';
import plansFields from '../../../fields/plansFields';
// Style
import './ModalUpdatePlan.scss';
import { nsPlan } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.plan
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalUpdatePlan
 */
function ModalUpdatePlan({ isOpen, setIsOpen, plan, setLaunchRefetch }) {

    // Constants
    const { uuid: planId, label, shouldAutoActivateSubscriptions } = plan || {};

    const formSchema = z.object({
        label: z.string().min(1, { message: '' }),
        autoActivate: z.boolean()
    });

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // Toast
    const { toast } = useToast();

    // Form definition
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            label: label,
            autoActivate: shouldAutoActivateSubscriptions
        }
    });

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity namespace={nsPlan} entity='modal.update_plan.success' />,
            variant: 'success'
        });
    };

    const onError = () => {
        const errorCode = 'UNKNOWN_ERROR';
        toast({
            description: <Entity namespace={nsPlan} entity={`toast.error.${errorCode}`} />,
            variant: 'danger'
        });
    };

    const updatePlan = actionsPlans.updatePlan(onSuccess, onError);

    // Functions
    const onFormSubmit = () => {
        const dataToSend = {
            [plansFields.LABEL.key]: form.getValues().label,
            [plansFields.SHOULD_AUTO_ACTIVATE_SUBSCRIPTIONS.key]: form.getValues().autoActivate
        };

        updatePlan.mutate({
            organizationId: rootOrganization.uuid,
            planId: planId,
            dataToSend: dataToSend
        });
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-xl'>
                <HeaderTitle title='modal.update_plan.title' namespace={nsPlan} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onFormSubmit)} className='update-plan-form'>
                            <FormField
                                control={form.control}
                                name='label'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>
                                            <Entity namespace={nsPlan} entity='keys.label' />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                disabled={updatePlan.isLoading}
                                                autoComplete='off'
                                                {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='autoActivate'
                                render={() => (
                                    <FormItem>
                                        <FormLabel>
                                            <Entity namespace={nsPlan} entity='keys.auto_activate' />
                                        </FormLabel>
                                        <FormControl>
                                            <Switch
                                                name='autoActivate'
                                                checked={form.getValues().autoActivate}
                                                disabled={updatePlan.isLoading}
                                                onCheckedChange={(value) => {
                                                    form.setValue('autoActivate', value);
                                                }}
                                                entity={form.getValues().autoActivate ? 'modal.update_plan.auto_activate.yes' : 'modal.update_plan.auto_activate.no'}
                                                namespace={nsPlan}
                                            />
                                        </FormControl>
                                        {form.getValues().autoActivate &&
                                            <div className='mt-4'>
                                                <Icon icon={ICONS.WARNING} className={cn('text-warning mr-1')} />
                                                <Entity entity='modal.update_plan.auto_activate.warning' namespace={nsPlan} className='text-sm font-normal' />
                                            </div>
                                        }
                                    </FormItem>
                                )
                                }
                            />
                        </form>
                    </Form>
                </ModalBody>
                <FooterForm
                    onSubmit={onFormSubmit}
                    isSubmitting={updatePlan.isLoading}
                    isSubmitDisabled={!form.formState.isValid}
                    showSubmitButton={true}
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdatePlan.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    plan: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func
};

export default ModalUpdatePlan;